import { FC, memo, useCallback, useContext, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { AppSwitcherFilledIcon } from '@getgo/chameleon-icons/react'
import { IconButton, Menu, MenuItem, Popover, Tooltip } from '@getgo/chameleon-web-react-wrapper'
import mixpanel from 'mixpanel-browser'

import { OrganizationContext } from 'components'
import { useApplications, useLocale } from 'hooks'
import { Application } from 'types/Application'
import {
  APPLICATION_ADMIN,
  APPLICATION_ANALYTICS,
  APPLICATION_BOSS_BILLING,
  APPLICATION_CALL_REPORTS,
  APPLICATION_CONTACT_CENTER,
  APPLICATION_INTEGRATIONS,
  APPLICATION_JIVE_BILLING,
  APPLICATION_UNIFIED_ADMIN,
} from 'utils/constants'
import { getApplicationName } from 'utils/getApplicationName'
import { mixpanelEventNames } from 'utils/mixpanel'
import { sortGoToAppsFirst } from 'utils/sortGoToAppsFirst'

enum ApplicationType {
  GoToConnect = 'GoToConnect',
  GoToAssist = 'GoToAssist',
  GoToMeeting = 'GoToMeeting',
  GoToTraining = 'GoToTraining',
  GoToWebinar = 'GoToWebinar',
  GoToResolve = 'GoToResolve',
  Default = 'Default', // Use when there is no type
}

interface ApplicationInfos {
  type: string
}

const MARKET_TEMPLATE_CC_URL_SUFFIX = '/connect/contact-center-as-a-service/contact-sales'

const applicationInfos: { [id: string]: ApplicationInfos } = {
  gotoconnect: { type: ApplicationType.GoToConnect },
  'admin-portal': { type: ApplicationType.GoToConnect },
  'unified-admin': { type: ApplicationType.GoToConnect },
  'jive-web': { type: ApplicationType.GoToConnect },
  'contact-center': { type: ApplicationType.GoToConnect },
  'jive-billing': { type: ApplicationType.GoToConnect },
  'call-reports': { type: ApplicationType.GoToConnect },
  'alert-center': { type: ApplicationType.GoToConnect },
  integrations: { type: ApplicationType.GoToConnect },
  'boss-billing': { type: ApplicationType.GoToConnect },
  'g2a-see-it': { type: ApplicationType.GoToAssist },
  'g2a-remote-support': { type: ApplicationType.GoToAssist },
  'g2a-service-desk': { type: ApplicationType.GoToAssist },
  g2m: { type: ApplicationType.GoToMeeting },
  g2w: { type: ApplicationType.GoToWebinar },
  g2t: { type: ApplicationType.GoToTraining },
  gotoresolve: { type: ApplicationType.GoToResolve },
}

const AppSwitcher: FC = () => {
  const t = useTranslate()
  const [open, setOpen] = useState<boolean>(false)
  const locale = useLocale()
  const pbx = useContext(OrganizationContext)

  const applicationList = useApplications()?.items || []
  const applications: Application[] = applicationList
    .map((application: Application) => ({
      ...application,
      type: applicationInfos[application.id]?.type || ApplicationType.Default,
    }))
    //Avoiding showing unified-admin app in the app switcher while already in the unified-admin app https://jira.ops.expertcity.com/browse/GAF-28
    .filter(
      (app) =>
        app.id !== APPLICATION_UNIFIED_ADMIN &&
        app.id !== APPLICATION_JIVE_BILLING &&
        app.id !== APPLICATION_BOSS_BILLING,
    )

  const addDomainToUrl = (url: string, domain?: string) => `${url.replace(/\/+$/, '')}/${domain || ''}`

  const getApplicationUrl = useCallback(
    (app: Application) => {
      switch (app.id) {
        case APPLICATION_ADMIN:
        case APPLICATION_ANALYTICS:
        case APPLICATION_JIVE_BILLING:
        case APPLICATION_CALL_REPORTS:
        case APPLICATION_INTEGRATIONS:
          return addDomainToUrl(app.url, pbx?.domain)
        case APPLICATION_CONTACT_CENTER: {
          const doesntHaveCCAccess = app.url.includes(MARKET_TEMPLATE_CC_URL_SUFFIX)
          return doesntHaveCCAccess ? app.url : addDomainToUrl(app.url, pbx?.domain)
        }
        default:
          return app.url
      }
    },
    [pbx],
  )

  if (!locale) {
    return null
  }

  return (
    <>
      <IconButton
        id="app-switcher-icon"
        data-testid="app-switcher-icon-btn"
        variant={open ? 'secondary' : 'primary'}
        size="medium"
        onClick={() => {
          mixpanel.track(mixpanelEventNames.APP_SWITCHER, {
            Action: `Toggled app switcher`,
          })
        }}
      >
        <AppSwitcherFilledIcon />
      </IconButton>
      <Tooltip triggerId="app-switcher-icon">{t('Change application')}</Tooltip>

      <Popover
        triggerId="app-switcher-icon"
        width={280}
        menu
        position="bottom-end"
        onChange={(e) => setOpen(e.detail.isOpen)}
      >
        <Menu>
          {applications?.sort(sortGoToAppsFirst(locale)).map((app) => (
            <MenuItem
              key={app.id}
              data-testid={app.id}
              onClick={() => {
                window.open(getApplicationUrl(app))
                mixpanel.track(mixpanelEventNames.APP_SWITCHER, {
                  Action: `Navigated to ${app.id}`,
                })
              }}
            >
              {getApplicationName(app.localizedDetails, locale)}
            </MenuItem>
          ))}
          {!applications.length && <MenuItem disabled>{t(`You don't have any applications`)}</MenuItem>}
        </Menu>
      </Popover>
    </>
  )
}

export default memo(AppSwitcher)
